/**
 * commonDiagnoses — Lista corta pero representativa de diagnósticos
 * frecuentes en atención primaria y especialidades comunes, basada en
 * códigos CIE-10. Cada entrada tiene:
 *   { code: "J00", label: "Rinofaringitis aguda (resfriado común)" }
 *
 * El autocompletado hace matching por `label` y por `code`.
 */
export const COMMON_DIAGNOSES = [
  // Respiratorios
  { code: "J00", label: "Rinofaringitis aguda (resfriado común)" },
  { code: "J02", label: "Faringitis aguda" },
  { code: "J03", label: "Amigdalitis aguda" },
  { code: "J04", label: "Laringitis aguda" },
  { code: "J06", label: "Infección respiratoria aguda alta" },
  { code: "J11", label: "Influenza (gripe)" },
  { code: "J18", label: "Neumonía" },
  { code: "J20", label: "Bronquitis aguda" },
  { code: "J30", label: "Rinitis alérgica" },
  { code: "J32", label: "Sinusitis crónica" },
  { code: "J44", label: "EPOC (Enfermedad pulmonar obstructiva crónica)" },
  { code: "J45", label: "Asma bronquial" },

  // Cardiovasculares
  { code: "I10", label: "Hipertensión arterial esencial" },
  { code: "I11", label: "Enfermedad cardiaca hipertensiva" },
  { code: "I20", label: "Angina de pecho" },
  { code: "I21", label: "Infarto agudo de miocardio" },
  { code: "I25", label: "Enfermedad isquémica crónica del corazón" },
  { code: "I48", label: "Fibrilación auricular" },
  { code: "I50", label: "Insuficiencia cardíaca" },
  { code: "I63", label: "Infarto cerebral (ACV isquémico)" },
  { code: "I83", label: "Varices de miembros inferiores" },

  // Metabólicas / Endocrinas
  { code: "E10", label: "Diabetes mellitus tipo 1" },
  { code: "E11", label: "Diabetes mellitus tipo 2" },
  { code: "E66", label: "Obesidad" },
  { code: "E78", label: "Dislipidemia / Hipercolesterolemia" },
  { code: "E03", label: "Hipotiroidismo" },
  { code: "E05", label: "Hipertiroidismo" },
  { code: "E86", label: "Deshidratación" },
  { code: "E87", label: "Trastorno hidroelectrolítico" },

  // Digestivos
  { code: "K21", label: "Reflujo gastroesofágico (ERGE)" },
  { code: "K25", label: "Úlcera gástrica" },
  { code: "K29", label: "Gastritis" },
  { code: "K30", label: "Dispepsia funcional" },
  { code: "K52", label: "Gastroenteritis aguda" },
  { code: "K58", label: "Síndrome de intestino irritable" },
  { code: "K59", label: "Estreñimiento" },
  { code: "K80", label: "Colelitiasis / Litiasis vesicular" },
  { code: "K81", label: "Colecistitis" },

  // Urinarios
  { code: "N10", label: "Pielonefritis aguda" },
  { code: "N17", label: "Insuficiencia renal aguda" },
  { code: "N18", label: "Enfermedad renal crónica" },
  { code: "N20", label: "Litiasis renal (cálculo renal)" },
  { code: "N30", label: "Cistitis" },
  { code: "N39", label: "Infección urinaria baja (ITU)" },
  { code: "N40", label: "Hiperplasia prostática benigna" },

  // Musculoesqueléticos
  { code: "M10", label: "Gota" },
  { code: "M15", label: "Osteoartrosis / Artrosis" },
  { code: "M25", label: "Dolor articular inespecífico" },
  { code: "M54", label: "Dorsalgia / Lumbalgia" },
  { code: "M62", label: "Contractura muscular" },
  { code: "M79", label: "Fibromialgia" },
  { code: "M81", label: "Osteoporosis" },

  // Dermatológicos
  { code: "L20", label: "Dermatitis atópica" },
  { code: "L23", label: "Dermatitis de contacto alérgica" },
  { code: "L30", label: "Dermatitis inespecífica / Eczema" },
  { code: "L40", label: "Psoriasis" },
  { code: "L50", label: "Urticaria" },
  { code: "L70", label: "Acné" },
  { code: "B35", label: "Dermatofitosis (tiña)" },
  { code: "B36", label: "Pitiriasis versicolor" },

  // Infecciosas
  { code: "A08", label: "Gastroenteritis viral" },
  { code: "A09", label: "Diarrea infecciosa inespecífica" },
  { code: "A90", label: "Dengue clásico" },
  { code: "A91", label: "Dengue hemorrágico" },
  { code: "B01", label: "Varicela" },
  { code: "B02", label: "Herpes zoster" },
  { code: "B05", label: "Sarampión" },
  { code: "B26", label: "Parotiditis (paperas)" },
  { code: "B34", label: "Infección viral no especificada" },

  // Neurológicos
  { code: "G40", label: "Epilepsia" },
  { code: "G43", label: "Migraña" },
  { code: "G44", label: "Cefalea tensional" },
  { code: "G47", label: "Trastornos del sueño / Insomnio" },
  { code: "R51", label: "Cefalea inespecífica" },

  // Salud mental
  { code: "F32", label: "Episodio depresivo" },
  { code: "F33", label: "Trastorno depresivo recurrente" },
  { code: "F41", label: "Trastorno de ansiedad" },
  { code: "F43", label: "Reacción a estrés y trastornos de adaptación" },
  { code: "F45", label: "Trastorno somatomorfo" },
  { code: "F51", label: "Insomnio no orgánico" },

  // Síntomas / Signos
  { code: "R05", label: "Tos" },
  { code: "R07", label: "Dolor torácico inespecífico" },
  { code: "R10", label: "Dolor abdominal" },
  { code: "R11", label: "Náuseas y vómitos" },
  { code: "R19", label: "Síntomas digestivos inespecíficos" },
  { code: "R42", label: "Mareo y vértigo" },
  { code: "R50", label: "Fiebre inespecífica" },
  { code: "R52", label: "Dolor no especificado" },
  { code: "R53", label: "Fatiga / Astenia" },

  // Ginecológicos / Obstétricos
  { code: "N92", label: "Trastornos menstruales (dismenorrea, menorragia)" },
  { code: "N93", label: "Hemorragia uterina anormal" },
  { code: "N94", label: "Síndrome premenstrual" },
  { code: "O20", label: "Hemorragia del embarazo temprano" },
  { code: "Z32", label: "Examen y control del embarazo" },
  { code: "Z30", label: "Anticoncepción / Planificación familiar" },

  // Hematológicos
  { code: "D50", label: "Anemia ferropénica" },
  { code: "D64", label: "Anemia no especificada" },

  // Oftalmo / ORL
  { code: "H10", label: "Conjuntivitis" },
  { code: "H66", label: "Otitis media" },
  { code: "H92", label: "Otalgia" },

  // Preventivos / Controles
  { code: "Z00", label: "Chequeo médico general" },
  { code: "Z23", label: "Vacunación" },
  { code: "Z71", label: "Consulta de consejería" },
];

/**
 * Filtra la lista por una query. Prioriza las coincidencias que empiezan
 * con la query, luego las que la contienen. Máximo 8 resultados.
 */
export function searchDiagnoses(query, limit = 8) {
  const q = (query || "").trim().toLowerCase();
  if (q.length < 2) return [];
  const starts = [];
  const includes = [];
  for (const d of COMMON_DIAGNOSES) {
    const l = d.label.toLowerCase();
    const c = d.code.toLowerCase();
    if (l.startsWith(q) || c.startsWith(q)) {
      starts.push(d);
    } else if (l.includes(q) || c.includes(q)) {
      includes.push(d);
    }
    if (starts.length >= limit) break;
  }
  return [...starts, ...includes].slice(0, limit);
}
